import React from "react";
import { Spinner } from "@chakra-ui/react";
import { Modal } from "newui/components/ui/Modal";
import { useModules } from "newui/features/RolesPermissions/hooks/useRoleMatrix";
import { useUserEffectivePermissions } from "newui/features/RolesPermissions/hooks/useUserEffectivePermissions";
import type {
	EffectiveCapabilityFlags,
	PermissionLevel,
} from "newui/features/RolesPermissions/types/RolesPermissions.type";

export interface UserAccessMatrixModalProps {
	isOpen: boolean;
	userId?: number;
	userName?: string;
	onClose: () => void;
}

interface LevelDisplay {
	fillClassName: string;
	label: string;
	textClassName: string;
}

// Mirrors PermissionMatrixEditor's LEVEL_CONFIG so the read-only view reads with
// the same colour language as the editable matrix (server-authoritative levels).
const LEVEL_DISPLAY: Record<PermissionLevel, LevelDisplay> = {
	full: { label: "Full", fillClassName: "bg-emerald-100 border-emerald-300", textClassName: "text-emerald-800" },
	view: { label: "View", fillClassName: "bg-blue-100 border-blue-300", textClassName: "text-blue-800" },
	comment: { label: "Comment", fillClassName: "bg-amber-100 border-amber-300", textClassName: "text-amber-900" },
	event: { label: "Event only", fillClassName: "bg-slate-100 border-slate-300", textClassName: "text-slate-700" },
	none: { label: "No access", fillClassName: "bg-gray-50 border-gray-200", textClassName: "text-gray-400" },
};

// Column order for the capability flags — the seven verbs the backend resolves.
const CAPABILITY_COLUMNS: Array<{ key: keyof EffectiveCapabilityFlags; label: string }> = [
	{ key: "view", label: "View" },
	{ key: "comment", label: "Comment" },
	{ key: "create", label: "Create" },
	{ key: "edit", label: "Edit" },
	{ key: "approve", label: "Approve" },
	{ key: "close", label: "Close" },
	{ key: "delete", label: "Delete" },
];

const CapabilityDot = ({ granted }: { granted: boolean }) => {
	if (granted) {
		return <span className="text-emerald-600" aria-label="allowed">✓</span>;
	}

	return <span className="text-gray-300" aria-label="not allowed">—</span>;
};

/**
 * PRO-413 #UM1 — read-only view of a single user's EFFECTIVE standing access,
 * rendered as a module × capability matrix. Server-authoritative: every cell
 * comes straight from GET /rbac/users/{user}/permissions; the FE never derives
 * access itself. Standing-only (H1) — resource/event-scoped grants are not shown.
 */
export const UserAccessMatrixModal = ({
	isOpen,
	userId,
	userName,
	onClose,
}: UserAccessMatrixModalProps) => {
	const modulesQuery = useModules();
	const permissionsQuery = useUserEffectivePermissions(isOpen ? userId : undefined);

	const isLoading = modulesQuery.isLoading || permissionsQuery.isLoading;
	const isError = modulesQuery.isError || permissionsQuery.isError;

	const modules = modulesQuery.data ?? [];
	const permissions = permissionsQuery.data;
	const roles = permissions?.roles ?? [];

	const title = userName ? `Access for ${userName}` : "User access";

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={title}
			description="Effective company-wide access, resolved from this user's assigned roles."
			size="xxl"
			primaryButton={{ text: "Close", btntype: "secondary", onClick: onClose }}
		>
			<div data-testid="user-access-matrix-modal" className="space-y-4">
				{roles.length > 0 && (
					<div className="flex flex-wrap items-center gap-2" data-testid="user-access-roles">
						<span className="text-sm font-medium text-gray-600">Roles:</span>
						{roles.map((role) => (
							<span
								key={role}
								className="rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700"
							>
								{role}
							</span>
						))}
					</div>
				)}

				{isLoading && (
					<div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-500">
						<Spinner size="sm" /> Loading access…
					</div>
				)}

				{!isLoading && isError && (
					<div className="py-10 text-center text-sm text-red-600" data-testid="user-access-error">
						Could not load this user&apos;s access. Please try again.
					</div>
				)}

				{!isLoading && !isError && (
					<div className="overflow-x-auto rounded-lg border border-gray-200">
						<table className="min-w-full border-collapse text-sm">
							<thead>
								<tr className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
									<th className="sticky left-0 z-10 bg-gray-50 px-3 py-2 font-semibold">Module</th>
									<th className="px-3 py-2 font-semibold">Level</th>
									{CAPABILITY_COLUMNS.map((column) => (
										<th key={column.key} className="px-3 py-2 text-center font-semibold">
											{column.label}
										</th>
									))}
								</tr>
							</thead>
							<tbody>
								{modules.map((module) => {
									const capability = permissions?.modules[module.key];
									const level: PermissionLevel = capability?.level ?? "none";
									const display = LEVEL_DISPLAY[level];

									return (
										<tr
											key={module.id}
											className="border-t border-gray-100"
											data-testid={`user-access-row-${module.key}`}
										>
											<th
												scope="row"
												className="sticky left-0 z-10 bg-white px-3 py-2 text-left font-medium text-gray-700"
											>
												{module.name}
											</th>
											<td className="px-3 py-2">
												<span
													className={`inline-block rounded border px-2 py-0.5 text-xs font-medium ${display.fillClassName} ${display.textClassName}`}
												>
													{display.label}
												</span>
											</td>
											{CAPABILITY_COLUMNS.map((column) => (
												<td key={column.key} className="px-3 py-2 text-center">
													<CapabilityDot granted={Boolean(capability?.can[column.key])} />
												</td>
											))}
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
			</div>
		</Modal>
	);
};
